/**
 * Procedural anomaly / unknown-signal particle-FX library.
 *
 * 10 distinct archetypes: each is a deterministic particle system (no Math.random
 * at runtime — only the cenotaph.ts hashF pattern). Archetypes are cheap Points
 * objects with mutated Float32 position buffers per frame. No steady-state allocation.
 *
 * Suppression rule: SignalRenderer already hides the beacon ring+core within 500 km
 * of anomaly signals. This library fills that void with sombre volumetric FX
 * (pulsating gas, accretion swirl, lensing ring, etc.) that appear ONLY when the
 * player is near enough to witness the anomaly up close.
 *
 * Floating-origin rule: render positions = worldPos − shipWorldPos (km).
 */
import * as THREE from 'three';
import { type Vec3, type SignalSource } from '@blackmarble/sim-core';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Radius (km) within which an anomaly FX is shown. */
const NEAR_KM = 3000;
const NEAR_KM_SQ = NEAR_KM * NEAR_KM;

/** Maximum simultaneous live AnomalyFx instances (pool). */
const MAX_LIVE = 2;

// ---------------------------------------------------------------------------
// Deterministic hash helper — same pattern as cenotaph.ts
// ---------------------------------------------------------------------------

const hashF = (n: number): number => {
  const s = Math.sin(n * 17.4321) * 43758.5453;
  return s - Math.floor(s);
};

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

interface XYZ { x: number; y: number; z: number }

interface AnomalyArchetype {
  readonly id: string;
  readonly particleCount: number;
  readonly color: number;
  readonly size: number;
  readonly blending?: THREE.Blending;
  layout(i: number, count: number, seed: number): XYZ;
  motion(base: XYZ, i: number, simTime: number, seed: number): XYZ;
}

// ---------------------------------------------------------------------------
// Layout helpers (keep each < 50 lines as pure fns)
// ---------------------------------------------------------------------------

/** Spherical shell layout — base positions on a sphere of radius r. */
function layoutSphere(i: number, count: number, seed: number, r: number): XYZ {
  const h1 = hashF(seed + i * 3.1);
  const h2 = hashF(seed + i * 7.7 + 1.1);
  const theta = Math.acos(2 * h1 - 1);
  const phi = h2 * Math.PI * 2;
  const jitter = 0.7 + hashF(seed + i * 2.3) * 0.6;
  return {
    x: r * jitter * Math.sin(theta) * Math.cos(phi),
    y: r * jitter * Math.sin(theta) * Math.sin(phi),
    z: r * jitter * Math.cos(theta),
  };
}

/** Flat disk layout (spiral arms). */
function layoutDisk(i: number, count: number, seed: number, r: number, arms: number): XYZ {
  const t = i / count;
  const arm = Math.floor(hashF(seed + i * 0.7) * arms);
  const angle = t * Math.PI * 2 * 3 + (arm / arms) * Math.PI * 2;
  const rad = (0.1 + t * 0.9) * r;
  const h = hashF(seed + i * 5.3 + 3) - 0.5;
  return { x: rad * Math.cos(angle), y: h * r * 0.08, z: rad * Math.sin(angle) };
}

/** Thin ring layout in XZ plane at radius r. */
function layoutRing(i: number, count: number, seed: number, r: number, spread: number): XYZ {
  const angle = (i / count) * Math.PI * 2 + hashF(seed + i * 1.9) * 0.18;
  const rad = r + (hashF(seed + i * 4.1) - 0.5) * spread;
  const yOff = (hashF(seed + i * 6.3) - 0.5) * spread * 0.3;
  return { x: rad * Math.cos(angle), y: yOff, z: rad * Math.sin(angle) };
}

/** Filament layout: a few radial spokes with sparse points. */
function layoutFilament(i: number, count: number, seed: number, r: number, spokes: number): XYZ {
  const spoke = Math.floor(hashF(seed + i * 2.9) * spokes);
  const spokeAngle = (spoke / spokes) * Math.PI * 2 + hashF(seed + spoke * 3.7) * 0.4;
  const t = hashF(seed + i * 5.1 + 2);
  const rad = t * r;
  const yOff = (hashF(seed + i * 8.3) - 0.5) * r * 0.05;
  return { x: rad * Math.cos(spokeAngle), y: yOff, z: rad * Math.sin(spokeAngle) };
}

/** Curtain layout: wide flat sheets drifting in Y. */
function layoutCurtain(i: number, count: number, seed: number, w: number, h: number): XYZ {
  const col = Math.floor(hashF(seed + i * 1.3) * 4); // 4 curtain sheets
  const xBase = (col / 3.0 - 0.5) * w;
  const xOff = (hashF(seed + i * 3.7) - 0.5) * w * 0.08;
  const y = (hashF(seed + i * 2.1) - 0.5) * h;
  const z = (hashF(seed + i * 4.9) - 0.5) * w * 0.6;
  return { x: xBase + xOff, y, z };
}

/** Large diffuse cloud — Gaussian-like scatter. */
function layoutCloud(i: number, count: number, seed: number, r: number): XYZ {
  const h1 = hashF(seed + i * 2.3);
  const h2 = hashF(seed + i * 5.7 + 0.5);
  const h3 = hashF(seed + i * 8.1 + 1.1);
  const theta = Math.acos(2 * h1 - 1);
  const phi = h2 * Math.PI * 2;
  const rad = (0.2 + h3 * 0.8) * r;
  return {
    x: rad * Math.sin(theta) * Math.cos(phi),
    y: rad * Math.sin(theta) * Math.sin(phi),
    z: rad * Math.cos(theta),
  };
}

/** Receding cone of motes (dark core variant, slightly off-center). */
function layoutReceding(i: number, count: number, seed: number, r: number): XYZ {
  const h1 = hashF(seed + i * 3.3);
  const h2 = hashF(seed + i * 7.1 + 0.3);
  const angle = h2 * Math.PI * 2;
  const coneRadius = (0.1 + h1 * 0.9) * r * h1; // narrower near core
  const depth = (1 - h1) * r * 1.2;
  return { x: coneRadius * Math.cos(angle), y: depth - r * 0.6, z: coneRadius * Math.sin(angle) };
}

/** Grid lattice layout. */
function layoutGrid(i: number, count: number, seed: number, extent: number): XYZ {
  const side = Math.ceil(Math.cbrt(count));
  const ix = i % side;
  const iy = Math.floor(i / side) % side;
  const iz = Math.floor(i / (side * side));
  const norm = (v: number): number => ((v / (side - 1 || 1)) * 2 - 1) * extent;
  return { x: norm(ix), y: norm(iy), z: norm(iz) };
}

/** Pulsed sphere: starts at centre, used for implosion-rebound cycle. */
function layoutBurst(i: number, count: number, seed: number, r: number): XYZ {
  const h1 = hashF(seed + i * 2.7);
  const h2 = hashF(seed + i * 6.3 + 0.7);
  const theta = Math.acos(2 * h1 - 1);
  const phi = h2 * Math.PI * 2;
  return { x: r * Math.sin(theta) * Math.cos(phi), y: r * Math.sin(theta) * Math.sin(phi), z: r * Math.cos(theta) };
}

/** Scattered mote layout (no structure). */
function layoutScatter(i: number, count: number, seed: number, r: number): XYZ {
  return layoutCloud(i, count, seed, r);
}

// ---------------------------------------------------------------------------
// Motion helpers
// ---------------------------------------------------------------------------

/** Slow breathing pulse: radially scale each base position by a breathing factor. */
function motionBreathe(base: XYZ, i: number, t: number, seed: number, rate: number, depth: number): XYZ {
  const phase = hashF(seed + i * 1.7) * Math.PI * 2;
  const s = 1 + Math.sin(t * rate + phase) * depth;
  return { x: base.x * s, y: base.y * s, z: base.z * s };
}

/** Accretion swirl: rotate in XZ plane around Y-axis at angular speed that
 *  depends on radial distance (faster near centre). */
function motionSwirl(base: XYZ, i: number, t: number, seed: number): XYZ {
  const r = Math.sqrt(base.x * base.x + base.z * base.z);
  const omega = 0.12 + 0.8 / (r * 0.006 + 1);
  const phaseOffset = hashF(seed + i * 0.3) * 0.08; // tiny per-particle phase jitter
  const curAngle = Math.atan2(base.z, base.x);
  const newAngle = curAngle + omega * t + phaseOffset;
  return { x: r * Math.cos(newAngle), y: base.y, z: r * Math.sin(newAngle) };
}

/** Radial wobble for lensing ring: perturb each particle radially + slight Y. */
function motionLens(base: XYZ, i: number, t: number, seed: number): XYZ {
  const phase = hashF(seed + i * 2.3) * Math.PI * 2;
  const wobble = Math.sin(t * 0.7 + phase) * 6;
  const angle = Math.atan2(base.z, base.x);
  const r = Math.sqrt(base.x * base.x + base.z * base.z) + wobble;
  const yOff = Math.sin(t * 1.1 + phase * 1.3) * 2;
  return { x: r * Math.cos(angle), y: base.y + yOff, z: r * Math.sin(angle) };
}

/** Arc sparks: rapid radial out-and-back along filament direction. */
function motionArc(base: XYZ, i: number, t: number, seed: number): XYZ {
  const phase = hashF(seed + i * 3.7) * Math.PI * 2;
  const pulse = Math.abs(Math.sin(t * 4.0 + phase));
  return { x: base.x * pulse, y: base.y + Math.sin(t * 8 + phase) * 1.5, z: base.z * pulse };
}

/** Aurora drift: each curtain drifts laterally + wavy Y perturbation. */
function motionAurora(base: XYZ, i: number, t: number, seed: number): XYZ {
  const phase = hashF(seed + i * 2.1) * Math.PI * 2;
  const xDrift = Math.sin(t * 0.15 + phase) * 18;
  const yWave = Math.sin(t * 0.3 + base.x * 0.02 + phase) * 12;
  return { x: base.x + xDrift, y: base.y + yWave, z: base.z };
}

/** Slow nebula drift: barely perceptible random drift. */
function motionNebula(base: XYZ, i: number, t: number, seed: number): XYZ {
  const px = hashF(seed + i * 1.1) * Math.PI * 2;
  const py = hashF(seed + i * 3.3 + 1) * Math.PI * 2;
  const pz = hashF(seed + i * 5.5 + 2) * Math.PI * 2;
  return {
    x: base.x + Math.sin(t * 0.03 + px) * 4,
    y: base.y + Math.sin(t * 0.025 + py) * 3,
    z: base.z + Math.sin(t * 0.028 + pz) * 4,
  };
}

/** Receding motes: each mote drifts slowly away from origin along its cone axis. */
function motionRecede(base: XYZ, i: number, t: number, seed: number): XYZ {
  const period = 60;
  const frac = ((t + hashF(seed + i * 2.9) * period) % period) / period;
  const len = Math.sqrt(base.x * base.x + base.y * base.y + base.z * base.z) || 1;
  const dx = (base.x / len) * frac * 1.2;
  const dy = (base.y / len) * frac * 1.2;
  const dz = (base.z / len) * frac * 1.2;
  return { x: base.x + dx, y: base.y + dy, z: base.z + dz };
}

/** Lattice pulse: contract/expand grid toward centre rhythmically. */
function motionLattice(base: XYZ, i: number, t: number, seed: number): XYZ {
  const phase = hashF(seed + i * 1.9) * Math.PI * 2;
  const s = 0.6 + 0.4 * Math.abs(Math.sin(t * 0.5 + phase));
  return { x: base.x * s, y: base.y * s, z: base.z * s };
}

/** Implosion-rebound: collapse to origin then burst outward in a loop. */
function motionImplosion(base: XYZ, i: number, t: number, seed: number): XYZ {
  const period = 6;
  const phase = hashF(seed + i * 2.7) * period;
  const frac = ((t + phase) % period) / period;
  // 0→0.4 collapse, 0.4→0.6 hold at centre, 0.6→1 burst
  let s: number;
  if (frac < 0.4) {
    s = 1 - frac / 0.4;
  } else if (frac < 0.6) {
    s = 0;
  } else {
    s = (frac - 0.6) / 0.4;
  }
  return { x: base.x * s, y: base.y * s, z: base.z * s };
}

/** Ember twinkle: slow drift + occasional brightness-encoded position shift. */
function motionEmber(base: XYZ, i: number, t: number, seed: number): XYZ {
  const px = hashF(seed + i * 2.0) * Math.PI * 2;
  const py = hashF(seed + i * 4.6 + 0.5) * Math.PI * 2;
  const pz = hashF(seed + i * 6.2 + 1.0) * Math.PI * 2;
  const speed = 0.04 + hashF(seed + i * 1.5) * 0.06;
  return {
    x: base.x + Math.sin(t * speed + px) * 6,
    y: base.y + Math.sin(t * speed * 0.7 + py) * 5,
    z: base.z + Math.sin(t * speed * 0.9 + pz) * 6,
  };
}

// ---------------------------------------------------------------------------
// The 10 archetypes
// ---------------------------------------------------------------------------

const ARCHETYPES: AnomalyArchetype[] = [
  // 0) Pulsating gas cloud — breathing spherical cloud
  {
    id: 'gas-cloud',
    particleCount: 320,
    color: 0x8a6fb0,
    size: 12,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutSphere(i, count, seed, 80),
    motion: (base, i, t, seed) => motionBreathe(base, i, t, seed, 0.4, 0.18),
  },

  // 1) Accretion swirl — particles spiral inward around Y axis
  {
    id: 'accretion-swirl',
    particleCount: 280,
    color: 0x9977cc,
    size: 8,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutDisk(i, count, seed, 110, 2),
    motion: (base, i, t, seed) => motionSwirl(base, i, t, seed),
  },

  // 2) Lensing shimmer — thin distorted ring, radial wobble
  {
    id: 'lensing-shimmer',
    particleCount: 200,
    color: 0xb3a0e0,
    size: 6,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutRing(i, count, seed, 95, 14),
    motion: (base, i, t, seed) => motionLens(base, i, t, seed),
  },

  // 3) Arc discharge — sparse bright sparks on a few filaments
  {
    id: 'arc-discharge',
    particleCount: 80,
    color: 0xd0c0ff,
    size: 9,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutFilament(i, count, seed, 120, 5),
    motion: (base, i, t, seed) => motionArc(base, i, t, seed),
  },

  // 4) Aurora/plasma ribbons — lateral drifting curtains
  {
    id: 'aurora-ribbons',
    particleCount: 260,
    color: 0x7a60a8,
    size: 14,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutCurtain(i, count, seed, 160, 90),
    motion: (base, i, t, seed) => motionAurora(base, i, t, seed),
  },

  // 5) Slow nebula bloom — large diffuse dust, very slow drift
  {
    id: 'nebula-bloom',
    particleCount: 400,
    color: 0x6a5090,
    size: 18,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutCloud(i, count, seed, 140),
    motion: (base, i, t, seed) => motionNebula(base, i, t, seed),
  },

  // 6) The Silence shadow — dark core + faint RECEDING violet motes
  {
    id: 'silence-shadow',
    particleCount: 160,
    color: 0x8a6fb0,
    size: 10,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutReceding(i, count, seed, 100),
    motion: (base, i, t, seed) => motionRecede(base, i, t, seed),
  },

  // 7) Interference lattice — particles toward a pulsing grid
  {
    id: 'interference-lattice',
    particleCount: 216,
    color: 0xaa88dd,
    size: 7,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutGrid(i, count, seed, 85),
    motion: (base, i, t, seed) => motionLattice(base, i, t, seed),
  },

  // 8) Implosion–rebound pulse — collapse → burst loop
  {
    id: 'implosion-rebound',
    particleCount: 240,
    color: 0xc0a0ff,
    size: 8,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutBurst(i, count, seed, 100),
    motion: (base, i, t, seed) => motionImplosion(base, i, t, seed),
  },

  // 9) Ember mote swarm — scattered slow twinkles
  {
    id: 'ember-motes',
    particleCount: 300,
    color: 0x9888c8,
    size: 5,
    blending: THREE.AdditiveBlending,
    layout: (i, count, seed) => layoutScatter(i, count, seed, 120),
    motion: (base, i, t, seed) => motionEmber(base, i, t, seed),
  },
];

// ---------------------------------------------------------------------------
// pickArchetype — deterministic seed → one of the 10
// ---------------------------------------------------------------------------

function pickArchetype(seed: number): AnomalyArchetype {
  const idx = Math.abs(seed) % ARCHETYPES.length;
  return ARCHETYPES[idx] as AnomalyArchetype;
}

// ---------------------------------------------------------------------------
// AnomalyFx — single particle system instance
// ---------------------------------------------------------------------------

export class AnomalyFx {
  readonly object: THREE.Points;
  private readonly posArr: Float32Array;
  private readonly basePositions: Float32Array;
  private readonly arch: AnomalyArchetype;
  private readonly seed: number;
  private readonly posAttr: THREE.BufferAttribute;

  constructor(arch: AnomalyArchetype, seed: number) {
    this.arch = arch;
    this.seed = seed;
    const n = arch.particleCount;
    this.posArr = new Float32Array(n * 3);
    this.basePositions = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const b = arch.layout(i, n, seed);
      this.basePositions[i * 3] = b.x;
      this.basePositions[i * 3 + 1] = b.y;
      this.basePositions[i * 3 + 2] = b.z;
      this.posArr[i * 3] = b.x;
      this.posArr[i * 3 + 1] = b.y;
      this.posArr[i * 3 + 2] = b.z;
    }
    const geo = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(this.posArr, 3);
    geo.setAttribute('position', this.posAttr);
    const mat = new THREE.PointsMaterial({
      color: arch.color,
      size: arch.size,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: arch.blending ?? THREE.AdditiveBlending,
      sizeAttenuation: true,
      toneMapped: false,
    });
    this.object = new THREE.Points(geo, mat);
    this.object.visible = false;
  }

  /** Per-frame update: advance motion and apply intensity. */
  update(simTime: number, intensity: number): void {
    const n = this.arch.particleCount;
    const motion = this.arch.motion;
    const seed = this.seed;
    for (let i = 0; i < n; i++) {
      const base: XYZ = {
        x: this.basePositions[i * 3] as number,
        y: this.basePositions[i * 3 + 1] as number,
        z: this.basePositions[i * 3 + 2] as number,
      };
      const p = motion(base, i, simTime, seed);
      this.posArr[i * 3] = p.x;
      this.posArr[i * 3 + 1] = p.y;
      this.posArr[i * 3 + 2] = p.z;
    }
    this.posAttr.needsUpdate = true;
    const mat = this.object.material as THREE.PointsMaterial;
    mat.opacity = 0.35 + intensity * 0.45;
    mat.size = this.arch.size * (0.6 + intensity * 0.4);
  }
}

// ---------------------------------------------------------------------------
// Pool entry — maps a signal id to a live AnomalyFx instance
// ---------------------------------------------------------------------------

interface PoolEntry {
  signalId: string;
  seed: number;
  fx: AnomalyFx;
}

// ---------------------------------------------------------------------------
// AnomalyFxRenderer — manages the pool, updates each frame
// ---------------------------------------------------------------------------

export class AnomalyFxRenderer {
  private readonly pool: PoolEntry[] = [];

  constructor(private readonly parent: THREE.Group) {}

  /**
   * Call each frame. Finds anomaly signals within NEAR_KM, assigns pooled
   * AnomalyFx instances, fades by distance, hides the rest.
   */
  update(
    signals: readonly SignalSource[],
    shipWorldPos: Vec3,
    simTime: number,
    dt: number,
  ): void {
    void dt; // reserved for future spawn-rate throttling
    // Gather candidate anomaly signals sorted by distance
    const candidates = collectCandidates(signals, shipWorldPos);

    // Determine which signal ids are active (up to MAX_LIVE nearest)
    const activeIds = new Set<string>();
    for (let i = 0; i < Math.min(candidates.length, MAX_LIVE); i++) {
      activeIds.add((candidates[i] as CandidateEntry).id);
    }

    // Evict pool entries whose signal is no longer active
    for (let i = this.pool.length - 1; i >= 0; i--) {
      const entry = this.pool[i] as PoolEntry;
      if (!activeIds.has(entry.signalId)) {
        entry.fx.object.visible = false;
        this.parent.remove(entry.fx.object);
        this.pool.splice(i, 1);
      }
    }

    // Activate/update entries for the active signals
    for (const candidate of candidates.slice(0, MAX_LIVE)) {
      const c = candidate as CandidateEntry;
      const intensity = computeIntensity(c.distKm);
      let entry = this.pool.find((e) => e.signalId === c.id);
      if (!entry) {
        entry = spawnPoolEntry(c.id, c.seed, this.parent);
        this.pool.push(entry);
      }
      const renderPos = computeRenderPos(c.sig, shipWorldPos);
      entry.fx.object.position.set(renderPos.x, renderPos.y, renderPos.z);
      entry.fx.object.visible = true;
      entry.fx.update(simTime, intensity);
    }
  }
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

interface CandidateEntry {
  id: string;
  seed: number;
  distKm: number;
  sig: SignalSource;
}

function collectCandidates(
  signals: readonly SignalSource[],
  shipWorldPos: Vec3,
): CandidateEntry[] {
  const out: CandidateEntry[] = [];
  for (const sig of signals) {
    if (sig.kind !== 'anomaly') continue;
    const dx = sig.pos.x - shipWorldPos.x;
    const dy = sig.pos.y - shipWorldPos.y;
    const dz = sig.pos.z - shipWorldPos.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq > NEAR_KM_SQ) continue;
    out.push({ id: sig.id, seed: sig.seed, distKm: Math.sqrt(distSq), sig });
  }
  out.sort((a, b) => a.distKm - b.distKm);
  return out;
}

/** Intensity: 1.0 at 0 km, 0.0 at NEAR_KM, smooth step. */
function computeIntensity(distKm: number): number {
  const t = Math.max(0, Math.min(1, 1 - distKm / NEAR_KM));
  return t * t * (3 - 2 * t); // smoothstep
}

function computeRenderPos(sig: SignalSource, shipWorldPos: Vec3): XYZ {
  return {
    x: sig.pos.x - shipWorldPos.x,
    y: sig.pos.y - shipWorldPos.y,
    z: sig.pos.z - shipWorldPos.z,
  };
}

function spawnPoolEntry(
  signalId: string,
  seed: number,
  parent: THREE.Group,
): PoolEntry {
  const arch = pickArchetype(seed);
  const fx = new AnomalyFx(arch, seed);
  parent.add(fx.object);
  return { signalId, seed, fx };
}
